import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  AppAuthValueProvider,
  createGuestAuthValue,
  useSyntheticProfileController,
} from "~/lib/auth/app-auth";
import { m } from "~/lib/i18n";

interface PrivyAppRuntimeProviderProps {
  children: ReactNode;
  onPendingLoginHandled?: () => void;
  pendingLogin?: boolean;
}

interface LazyPrivyAppProviderProps {
  children: ReactNode;
  mountRuntimeAfterPublicBootstrap?: boolean;
  pathname: string;
  publicBootstrap?: boolean;
}

type PrivyAppRuntimeProviderComponent = (
  props: PrivyAppRuntimeProviderProps,
) => ReactNode;
type PrivyAppRuntimeProviderModule = {
  PrivyAppRuntimeProvider: PrivyAppRuntimeProviderComponent;
};
type PrivyAppRuntimeProviderLoader =
  () => Promise<PrivyAppRuntimeProviderModule>;

const loadPrivyAppRuntimeProviderModuleDefault: PrivyAppRuntimeProviderLoader =
  async () => await import("./privy-app-runtime-provider");

let cachedPrivyAppRuntimeProvider: PrivyAppRuntimeProviderComponent | null =
  null;
let cachedPrivyAppRuntimeProviderPromise: Promise<PrivyAppRuntimeProviderComponent> | null =
  null;
let loadPrivyAppRuntimeProviderModule =
  loadPrivyAppRuntimeProviderModuleDefault;

function getLoadedPrivyAppRuntimeProvider(): PrivyAppRuntimeProviderComponent | null {
  return cachedPrivyAppRuntimeProvider;
}

export function preloadPrivyAppRuntimeProvider(): Promise<PrivyAppRuntimeProviderComponent> {
  if (cachedPrivyAppRuntimeProvider) {
    return Promise.resolve(cachedPrivyAppRuntimeProvider);
  }

  if (!cachedPrivyAppRuntimeProviderPromise) {
    cachedPrivyAppRuntimeProviderPromise = loadPrivyAppRuntimeProviderModule()
      .then((module) => {
        cachedPrivyAppRuntimeProvider = module.PrivyAppRuntimeProvider;
        return module.PrivyAppRuntimeProvider;
      })
      .catch((error: unknown) => {
        cachedPrivyAppRuntimeProviderPromise = null;
        throw error;
      });
  }

  return cachedPrivyAppRuntimeProviderPromise;
}

export function __setPrivyAppRuntimeProviderLoaderForTests(
  loader: PrivyAppRuntimeProviderLoader,
): void {
  loadPrivyAppRuntimeProviderModule = loader;
  cachedPrivyAppRuntimeProvider = null;
  cachedPrivyAppRuntimeProviderPromise = null;
}

export function __resetLazyPrivyAppProviderForTests(): void {
  loadPrivyAppRuntimeProviderModule = loadPrivyAppRuntimeProviderModuleDefault;
  cachedPrivyAppRuntimeProvider = null;
  cachedPrivyAppRuntimeProviderPromise = null;
}

function PrivyAppRuntimeFallbackShell() {
  return (
    <div
      aria-busy="true"
      className="min-h-screen bg-background text-foreground"
      data-testid="lazy-privy-runtime-fallback"
      role="status"
    >
      <span className="sr-only">
        {m.components_lazy_privy_app_provider_loading_label()}
      </span>
    </div>
  );
}

export function LazyPrivyAppProvider(props: LazyPrivyAppProviderProps) {
  const controller = useSyntheticProfileController();
  const mountRuntimeAfterPublicBootstrap =
    props.mountRuntimeAfterPublicBootstrap ?? false;
  const publicBootstrap = props.publicBootstrap ?? false;
  const [RuntimeProvider, setRuntimeProvider] =
    useState<PrivyAppRuntimeProviderComponent | null>(
      getLoadedPrivyAppRuntimeProvider,
    );
  const [pendingLogin, setPendingLogin] = useState(false);
  const [shouldUsePrivyRuntime, setShouldUsePrivyRuntime] = useState(
    () => !publicBootstrap,
  );

  const recoverGuestRuntime = useCallback(() => {
    setPendingLogin(false);
    if (publicBootstrap) {
      setShouldUsePrivyRuntime(false);
    }
  }, [publicBootstrap]);

  useEffect(() => {
    if (!publicBootstrap) {
      setShouldUsePrivyRuntime(true);
    }
  }, [publicBootstrap]);

  useEffect(() => {
    if (
      !publicBootstrap ||
      shouldUsePrivyRuntime ||
      typeof window === "undefined"
    ) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | null = null;
    let frameId: number | null = null;
    const preloadAfterPaint = () => {
      timeoutId = window.setTimeout(() => {
        if (cancelled) {
          return;
        }

        void preloadPrivyAppRuntimeProvider()
          .then((loadedProvider) => {
            if (cancelled) {
              return;
            }

            if (mountRuntimeAfterPublicBootstrap) {
              setRuntimeProvider(() => loadedProvider);
              setShouldUsePrivyRuntime(true);
            }
          })
          .catch(() => undefined);
      }, 0);
    };

    if (typeof window.requestAnimationFrame === "function") {
      frameId = window.requestAnimationFrame(preloadAfterPaint);
    } else {
      preloadAfterPaint();
    }

    return () => {
      cancelled = true;
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    mountRuntimeAfterPublicBootstrap,
    publicBootstrap,
    shouldUsePrivyRuntime,
  ]);

  useEffect(() => {
    if (RuntimeProvider || !shouldUsePrivyRuntime) {
      return;
    }

    let cancelled = false;
    void preloadPrivyAppRuntimeProvider()
      .then((loadedProvider) => {
        if (!cancelled) {
          setRuntimeProvider(() => loadedProvider);
        }
      })
      .catch(() => {
        if (!cancelled) {
          recoverGuestRuntime();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [RuntimeProvider, recoverGuestRuntime, shouldUsePrivyRuntime]);

  const requestPrivyLogin = useCallback(async () => {
    setPendingLogin(true);
    setShouldUsePrivyRuntime(true);
    try {
      const loadedProvider = await preloadPrivyAppRuntimeProvider();
      setRuntimeProvider(() => loadedProvider);
    } catch (error) {
      recoverGuestRuntime();
      throw error;
    }
  }, [recoverGuestRuntime]);

  const guestValue = useMemo(
    () => ({
      ...createGuestAuthValue(controller),
      login: requestPrivyLogin,
      ready: !pendingLogin,
    }),
    [controller, pendingLogin, requestPrivyLogin],
  );

  if (RuntimeProvider && shouldUsePrivyRuntime) {
    return (
      <RuntimeProvider
        onPendingLoginHandled={() => {
          setPendingLogin(false);
        }}
        pendingLogin={pendingLogin}
      >
        {props.children}
      </RuntimeProvider>
    );
  }

  if (publicBootstrap && (!RuntimeProvider || !shouldUsePrivyRuntime)) {
    return (
      <AppAuthValueProvider value={guestValue}>
        {props.children}
      </AppAuthValueProvider>
    );
  }

  return <PrivyAppRuntimeFallbackShell />;
}
